/**
 * PrivacyPolicy.tsx
 * Design: Serif editorial, stone/cream palette, clean document layout
 */

import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const LAST_UPDATED = "April 5, 2026";
const CONTACT_EMAIL = "hello@brasovgateway.com";
const COMPANY_NAME = "Brașov Gateway";
const SITE_URL = "https://brasovgateway.com";

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy — Brașov Gateway</title>
        <meta name="description" content="Privacy Policy for Brașov Gateway. Learn how we collect, use, and protect your personal data in accordance with GDPR." />
        <meta property="og:locale" content="en_US" />
        <link rel="canonical" href={`${SITE_URL}/privacy-policy`} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <SiteHeader />

      <main className="pt-16 min-h-screen" style={{ backgroundColor: "oklch(0.98 0.005 60)" }}>
        {/* Header */}
        <div className="py-16 border-b border-stone-200" style={{ backgroundColor: "oklch(0.96 0.008 60)" }}>
          <div className="max-w-3xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
              <span className="text-xs uppercase tracking-widest text-stone-500" style={{ fontFamily: "'Source Sans 3', sans-serif" }}>
                Legal
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Privacy Policy
            </h1>
            <p className="text-stone-500 text-sm font-light">
              Last updated: {LAST_UPDATED}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="prose prose-stone max-w-none" style={{ fontFamily: "'Source Sans 3', sans-serif" }}>

            {/* Intro */}
            <p className="text-stone-600 text-base leading-relaxed mb-10">
              {COMPANY_NAME} ("we", "us", or "our") operates the website{" "}
              <a href={SITE_URL} className="text-stone-800 underline">{SITE_URL}</a>. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website or use our services, in accordance with the General Data Protection Regulation (GDPR) and applicable Romanian law.
            </p>

            <Section title="1. Data Controller">
              <p>
                The data controller responsible for your personal data is:
              </p>
              <div className="bg-stone-100 rounded p-4 mt-3 text-sm text-stone-700 space-y-1">
                <p><strong>{COMPANY_NAME}</strong></p>
                <p>Brașov, Romania</p>
                <p>Email: <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a></p>
              </div>
            </Section>

            <Section title="2. What Data We Collect">
              <p>We may collect the following categories of personal data:</p>
              <ul className="list-disc pl-5 space-y-2 text-stone-600 mt-3">
                <li><strong>Contact information</strong> — name, email address, phone number, and any message content you provide when submitting our contact form.</li>
                <li><strong>Newsletter subscription data</strong> — your email address and consent timestamp when you subscribe to our newsletter.</li>
                <li><strong>Usage data</strong> — anonymised information about how you interact with our website (pages visited, time spent, referring URL), collected via analytics tools.</li>
                <li><strong>Communication records</strong> — records of correspondence between you and us, including emails and call notes.</li>
              </ul>
              <p className="mt-4">
                We do not collect sensitive personal data (such as health, financial, or biometric data) through this website.
              </p>
            </Section>

            <Section title="3. Legal Basis for Processing">
              <p>We process your personal data on the following legal bases under GDPR Article 6:</p>
              <ul className="list-disc pl-5 space-y-2 text-stone-600 mt-3">
                <li><strong>Consent (Art. 6(1)(a))</strong> — for newsletter subscriptions and optional marketing communications. You may withdraw consent at any time.</li>
                <li><strong>Contractual necessity (Art. 6(1)(b))</strong> — to respond to your enquiries and provide the services you have requested.</li>
                <li><strong>Legitimate interests (Art. 6(1)(f))</strong> — for website analytics and improving our services, where these interests are not overridden by your rights.</li>
                <li><strong>Legal obligation (Art. 6(1)(c))</strong> — where required by applicable law.</li>
              </ul>
            </Section>

            <Section title="4. How We Use Your Data">
              <p>We use the personal data we collect to:</p>
              <ul className="list-disc pl-5 space-y-2 text-stone-600 mt-3">
                <li>Respond to your enquiries and provide relocation, real estate, or business advisory services;</li>
                <li>Send you our newsletter and relevant updates (only with your explicit consent);</li>
                <li>Improve the content and functionality of our website;</li>
                <li>Comply with legal obligations;</li>
                <li>Prevent fraud and ensure the security of our services.</li>
              </ul>
              <p className="mt-4">
                We do not sell, rent, or trade your personal data to third parties for marketing purposes.
              </p>
            </Section>

            <Section title="5. Data Sharing">
              <p>We may share your data with the following categories of third parties, strictly as necessary:</p>
              <ul className="list-disc pl-5 space-y-2 text-stone-600 mt-3">
                <li><strong>Email service providers</strong> — to send newsletters and transactional emails (e.g. Brevo / Mailchimp). These providers act as data processors under a Data Processing Agreement.</li>
                <li><strong>Scheduling tools</strong> — if you book a call via Calendly or a similar platform, their own privacy policy applies.</li>
                <li><strong>Analytics providers</strong> — anonymised usage data may be processed by analytics tools. We do not share personally identifiable information with analytics providers.</li>
                <li><strong>Legal authorities</strong> — where required by law, court order, or regulatory obligation.</li>
              </ul>
              <p className="mt-4">
                All third-party processors are selected to ensure an adequate level of data protection consistent with GDPR requirements.
              </p>
            </Section>

            <Section title="6. Data Retention">
              <p>We retain your personal data only for as long as necessary for the purposes described in this policy:</p>
              <ul className="list-disc pl-5 space-y-2 text-stone-600 mt-3">
                <li><strong>Contact form submissions</strong> — retained for up to 2 years from the date of last communication, unless a longer retention period is required by law.</li>
                <li><strong>Newsletter subscriptions</strong> — retained until you unsubscribe or withdraw consent.</li>
                <li><strong>Analytics data</strong> — anonymised and retained in aggregate form; no personal identifiers are stored beyond the session.</li>
              </ul>
            </Section>

            <Section title="7. Your Rights Under GDPR">
              <p>As a data subject, you have the following rights:</p>
              <ul className="list-disc pl-5 space-y-2 text-stone-600 mt-3">
                <li><strong>Right of access</strong> — you may request a copy of the personal data we hold about you.</li>
                <li><strong>Right to rectification</strong> — you may request correction of inaccurate or incomplete data.</li>
                <li><strong>Right to erasure</strong> — you may request deletion of your personal data ("right to be forgotten"), subject to legal obligations.</li>
                <li><strong>Right to restriction</strong> — you may request that we restrict processing of your data in certain circumstances.</li>
                <li><strong>Right to data portability</strong> — you may request your data in a structured, machine-readable format.</li>
                <li><strong>Right to object</strong> — you may object to processing based on legitimate interests or for direct marketing purposes.</li>
                <li><strong>Right to withdraw consent</strong> — where processing is based on consent, you may withdraw it at any time without affecting the lawfulness of prior processing.</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-stone-800">{CONTACT_EMAIL}</a>. We will respond within 30 days.
              </p>
              <p className="mt-3">
                You also have the right to lodge a complaint with the Romanian data protection authority (ANSPDCP) at{" "}
                <a href="https://www.dataprotection.ro" target="_blank" rel="noopener noreferrer" className="underline text-stone-800">www.dataprotection.ro</a>.
              </p>
            </Section>

            <Section title="8. Cookies and Tracking">
              <p>
                Our website may use cookies and similar technologies to improve your browsing experience and analyse site traffic. Cookies are small text files stored on your device.
              </p>
              <p className="mt-3">
                We use the following types of cookies:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-stone-600 mt-3">
                <li><strong>Strictly necessary cookies</strong> — required for the website to function. These cannot be disabled.</li>
                <li><strong>Analytics cookies</strong> — used to understand how visitors interact with the site (e.g. pages visited, session duration). Data is anonymised where possible.</li>
              </ul>
              <p className="mt-4">
                You can control cookies through your browser settings. Disabling certain cookies may affect the functionality of the website.
              </p>
            </Section>

            <Section title="9. Data Security">
              <p>
                We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. Our website is served over HTTPS. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </Section>

            <Section title="10. International Transfers">
              <p>
                Your data is primarily processed within the European Economic Area (EEA). Where we use third-party service providers that may transfer data outside the EEA, we ensure appropriate safeguards are in place (such as Standard Contractual Clauses approved by the European Commission).
              </p>
            </Section>

            <Section title="11. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. The "Last updated" date at the top of this page indicates when the policy was last revised. We encourage you to review this page periodically.
              </p>
            </Section>

            <Section title="12. Contact Us">
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or the processing of your personal data, please contact us:
              </p>
              <div className="bg-stone-100 rounded p-4 mt-3 text-sm text-stone-700 space-y-1">
                <p><strong>{COMPANY_NAME}</strong></p>
                <p>Brașov, Romania</p>
                <p>Email: <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a></p>
              </div>
            </Section>

          </div>
        </div>
      </main>

      <SiteFooter />
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10">
      <h2 className="text-xl font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
        {title}
      </h2>
      <div className="text-stone-600 text-base leading-relaxed space-y-3">
        {children}
      </div>
    </div>
  );
}
